import { createContext, useContext, type Dispatch, type DragEvent, type MouseEvent } from 'react';
import type { SlotBoard } from './board.ts';
import type { DepthOrder, Player, RuleSummary } from '../types.ts';
import type { Action, SquadState } from './store.ts';

/** Props that make any player chip clickable to select and draggable onto the pitch. */
export interface PlayerHandleProps {
  onClick: (event: MouseEvent) => void;
  draggable: true;
  onDragStart: (event: DragEvent) => void;
  onDragEnd: () => void;
}

export interface SquadContextValue {
  state: SquadState;
  dispatch: Dispatch<Action>;
  /**
   * The whole squad with the depth chart's arrangement applied. Prefer this over
   * `state.players`, which still carries the untouched dataset positions.
   */
  players: Player[];
  /** The resolved depth chart — every player listed exactly once, under one position. */
  depthOrder: DepthOrder;
  /** The current formation laid out over the squad; the poster and pitch share it. */
  board: SlotBoard;
  /** Players still at the club — planned exits included, completed exits excluded. */
  owned: Player[];
  /** Players on the first-team list. */
  firstTeam: Player[];
  /** Rule summary over the first-team list. Drives the TFF and UEFA cards. */
  listRules: RuleSummary;
  /** Rule summary over everyone still owned. Drives the KPI row and the wage bar. */
  squadRules: RuleSummary;
  selected: Player | null;
  /** Squad-wide unique short name: forename for Turkish players, surname otherwise. */
  shortName: (p: Player) => string;
  /** The short name upper-cased for the poster, with locale-correct casing. */
  posterName: (p: Player) => string;
  /** Select-on-click plus drag-to-place props for one player. */
  playerHandles: (id: string) => PlayerHandleProps;
}

/**
 * Kept apart from the provider component so that this module exports no components —
 * React Fast Refresh only keeps state across edits when a module is components-only.
 */
export const SquadContext = createContext<SquadContextValue | null>(null);

export function useSquad(): SquadContextValue {
  const value = useContext(SquadContext);
  if (!value) throw new Error('useSquad must be used inside <SquadProvider>');
  return value;
}
